import type {
  iConfirmBookingBlockData,
  iCTABlockData,
  iHolidayQuoteContentBlocks,
  iHolidayQuoteX,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import type { iBrandQuoteLegalConfig } from "@/@sdk/brands/brand-model";

export type QuoteBookingBlockData = iCTABlockData | iConfirmBookingBlockData;

export interface ResolvedBookingBlockOptions {
  includeBookingConfirmationViaStripeDeposit: boolean;
  includeBookingConfirmation: boolean;
  includeDeferredBookingConfirmation: boolean;
}

export interface QuotePricingDepositOption {
  blockId: string;
  name: string;
  totalPrice: number;
  depositRequired: number;
  currency: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: "£",
  USD: "$",
  EUR: "€",
  INR: "₹",
  LKR: "Rs.",
};

export function formatQuoteCurrency(amount: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] ?? `${currency} `;
  return `${symbol}${amount.toFixed(2)}`;
}

export function resolveBookingBlockOptions(
  data: QuoteBookingBlockData
): ResolvedBookingBlockOptions {
  const hasExplicitOption =
    data.includeBookingConfirmationViaStripeDeposit !== undefined ||
    data.includeBookingConfirmation !== undefined ||
    data.includeDeferredBookingConfirmation !== undefined;

  if (!hasExplicitOption) {
    return {
      includeBookingConfirmationViaStripeDeposit: false,
      includeBookingConfirmation: false,
      includeDeferredBookingConfirmation: true,
    };
  }

  return {
    includeBookingConfirmationViaStripeDeposit: Boolean(
      data.includeBookingConfirmationViaStripeDeposit
    ),
    includeBookingConfirmation: Boolean(data.includeBookingConfirmation),
    includeDeferredBookingConfirmation: Boolean(
      data.includeDeferredBookingConfirmation
    ),
  };
}

export function resolveQuoteLegalUrls(
  quoteLegalConfig?: iBrandQuoteLegalConfig,
  blockData?: Pick<
    QuoteBookingBlockData,
    "termsAndConditions" | "bookingConditions"
  >
): {
  termsAndConditionsUrl?: string;
  bookingConditionsUrl?: string;
} {
  return {
    termsAndConditionsUrl:
      quoteLegalConfig?.termsAndConditionsUrl || blockData?.termsAndConditions,
    bookingConditionsUrl:
      quoteLegalConfig?.bookingConditionsUrl || blockData?.bookingConditions,
  };
}

export function collectQuotePricingDepositOptions(
  quote: iHolidayQuoteX
): QuotePricingDepositOption[] {
  const allBlocks: iHolidayQuoteContentBlocks[] = [
    ...(quote.preItineraryBlocks ?? []),
    ...(quote.postItineraryBlocks ?? []),
    ...(quote.pricingBlocks ?? []),
    ...(quote.postPricingBlocks ?? []),
  ];

  return allBlocks
    .filter(
      (block) =>
        block.type === "pricing_option" &&
        block.id &&
        block.pricingOptionData &&
        Number(block.pricingOptionData.depositRequired ?? 0) > 0
    )
    .map((block) => ({
      blockId: block.id!,
      name: block.pricingOptionData!.name,
      totalPrice: block.pricingOptionData!.totalPrice,
      depositRequired: Number(block.pricingOptionData!.depositRequired),
      currency: block.pricingOptionData!.currency ?? "GBP",
    }));
}
